import React, { useMemo, useRef, useEffect } from "react";
import dateFormat, { i18n } from "dateformat";
import { useAppDispatch } from "@redux/store";
import { setDate } from "@redux/rent/slice";
import { setRentDateToStorage } from "@utils/index";
import { moscowNow } from "@utils/date/moscowNow";
import { useBookingWindow } from "@/hooks/useBookingWindow";
import { dayNamesForRent } from "@resources/locale";
import styles from "./WeekDaysMobile.module.scss";

i18n.dayNames = dayNamesForRent;

interface WeekDaysMobileProps {
  date: string;
  selectedDate?: string;
}

export const WeekDaysMobile: React.FC<WeekDaysMobileProps> = ({ date, selectedDate }) => {
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedDayRef = useRef<HTMLButtonElement>(null);

  const { maxDate: windowMaxDate } = useBookingWindow();
  const maxTime = windowMaxDate.getTime();

  const weekDays = useMemo(() => {
    if (!date) {
      return [];
    }

    const parsedDate = Date.parse(date);
    if (isNaN(parsedDate)) {
      return [];
    }

    const currentDate = new Date(parsedDate);
    if (isNaN(currentDate.getTime())) {
      return [];
    }

    const viewedMonth = currentDate.getMonth();

    const startDate = new Date(currentDate);
    startDate.setDate(currentDate.getDate() - 3);

    if (isNaN(startDate.getTime())) {
      return [];
    }

    // Граница продаж с бэка (учитывает ранний доступ тренеров)
    const maxDateObj = new Date(maxTime);
    maxDateObj.setHours(23, 59, 59, 999);

    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);

      if (isNaN(day.getTime())) {
        continue;
      }

      const dayDate = new Date(day).setHours(0, 0, 0, 0);
      const nowDate = moscowNow().setHours(0, 0, 0, 0);
      const disableDate = dayDate < nowDate || dayDate > maxDateObj.getTime();
      const isOtherMonth = day.getMonth() !== viewedMonth;

      days.push({
        dateString: dateFormat(day, "isoDate"),
        dayName: dateFormat(day, "dddd"),
        formattedDate: dateFormat(day, "dd.mm.yyyy"),
        textDateName: dateFormat(day, "ddd"),
        textDate: dateFormat(day, "dd.mm.yyyy"),
        dayNumber: dateFormat(day, "dd"),
        disabled: disableDate,
        isOtherMonth: isOtherMonth,
      });
    }
    
    return days;
  }, [date, maxTime]);

  useEffect(() => {
    if (selectedDayRef.current && containerRef.current) {
      const container = containerRef.current;
      const selectedButton = selectedDayRef.current;
      
      const containerWidth = container.offsetWidth;
      const buttonLeft = selectedButton.offsetLeft;
      const buttonWidth = selectedButton.offsetWidth;
      const buttonCenter = buttonLeft + buttonWidth / 2;
      const scrollLeft = buttonCenter - containerWidth / 2;
      
      container.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    }
  }, [date, weekDays]);

  const handleDayClick = (dayDate: string) => {
    setRentDateToStorage(dayDate);
    dispatch(setDate(dayDate));
  };

  const isSelected = (dayDate: string) => {
    return dayDate === (selectedDate || date);
  };

  const isToday = (dayDate: string) => {
    const today = dateFormat(moscowNow(), "isoDate");
    return dayDate === today;
  };

  if (!weekDays || weekDays.length === 0) {
    return null;
  }

  return (
    <div className={styles.weekDays} ref={containerRef}>
      {weekDays.map((day) => (
        <button
          key={day.dateString}
          ref={isSelected(day.dateString) ? selectedDayRef : null}
          className={`${styles.weekDay} ${day.disabled ? styles.weekDay_disabled : ""} ${
            day.isOtherMonth && !isSelected(day.dateString) ? styles.weekDay_otherMonth : ""
          }`}
          onClick={() => handleDayClick(day.dateString)}
          disabled={day.disabled}
        >
          <span className={styles.weekDay_textDateName}>
            {day.textDateName}
          </span>
          <span className={`${styles.weekDay_number} ${isSelected(day.dateString) ? styles.weekDay_number_selected : ""} ${
            isToday(day.dateString) && !isSelected(day.dateString) ? styles.weekDay_number_today : ""
          }`}>
            {day.dayNumber}
          </span>
        </button>
      ))}
    </div>
  );
};
